import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { BillingAgreement, Currency } from '@freelancer/datastore/collections';
import { Deposits } from '@freelancer/deposits';
import {
  PaymentsErrorType,
  PaymentsMessagingService,
  PaymentsResultStatus,
} from '@freelancer/payments-messaging';
import { PaymentsUtils } from '@freelancer/payments-utils';
import { ThreatmetrixService } from '@freelancer/threatmetrix';
import { PictureDisplay, PictureImage } from '@freelancer/ui/picture';
import { DepositMethodApi } from 'api-typings/payments/payments';
import { SourceDisplayMode } from '../../../../common/source-template/source-template.component';
import { DepositData } from '../../../../common/types';

@Component({
  selector: 'app-virtual-billing-agreement',
  template: `
    <app-source-template
      [group]="group"
      [sourceRadioValue]="agreement.id"
      [sourceDisplayMode]="SourceDisplayMode.LIST_ITEM_WITH_HEADER"
    >
      <app-source-template-header>
        <app-source-template-header-title>
          {{ this.agreement?.virtualPaymentMethod?.name }} ({{
            billingAgreementCurrency?.code
          }})
        </app-source-template-header-title>
        <app-source-template-logo
          [image]="paymentMethodImage"
        ></app-source-template-logo>
      </app-source-template-header>
    </app-source-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualBillingAgreementComponent implements OnInit {
  PictureDisplay = PictureDisplay;
  SourceDisplayMode = SourceDisplayMode;

  billingAgreementCurrency: Currency | undefined;
  paymentMethodImage: PictureImage;

  images = {
    virtualBillingAgreement: {
      src: this.paymentsUtils.getMethodLogoPath('virtual'),
      alt: 'Virtual payment method logo',
    },
    enterpriseBillingAgreement: {
      src: this.paymentsUtils.getMethodLogoPath(
        DepositMethodApi.ENTERPRISE_BILLING,
      ),
      alt: 'Enterprise billing agreement payment method logo',
    },
    teamBillingAgreement: {
      src: this.paymentsUtils.getMethodLogoPath(DepositMethodApi.TEAM_BILLING),
      alt: 'Team billing agreement payment method logo',
    },
  };

  @Input() agreement: BillingAgreement;
  @Input() cartId?: number;
  @Input() group: FormGroup;
  @Input() currencies: readonly Currency[];
  @Input() trackingToken: string;
  @Input() set depositData(depositData: DepositData) {
    if (depositData) {
      this.handleDepositDataChanges(depositData);
    }
  }

  @Output() paymentCurrencySelected = new EventEmitter<Currency>();

  constructor(
    private th: ThreatmetrixService,
    private paymentsMessaging: PaymentsMessagingService,
    private deposits: Deposits,
    private paymentsUtils: PaymentsUtils,
  ) {}

  ngOnInit() {
    this.billingAgreementCurrency = this.currencies.find(
      c => c.id === this.agreement.currencyId,
    );
    switch (this.agreement.depositMethod) {
      case DepositMethodApi.ENTERPRISE_BILLING:
        this.paymentMethodImage = this.images.enterpriseBillingAgreement;
        break;
      case DepositMethodApi.TEAM_BILLING:
        this.paymentMethodImage = this.images.teamBillingAgreement;
        break;
      default:
        this.paymentMethodImage = this.images.virtualBillingAgreement;
    }
  }

  handleDepositDataChanges(depositData: DepositData) {
    this.deposits
      .authorization(
        this.agreement.depositMethod,
        this.agreement.id,
        depositData.depositAmount,
        depositData.depositCurrency.id,
        depositData.paymentCurrency.id,
        this.th.getSession(),
        this.trackingToken,
        this.cartId,
      )
      .toPromise()
      .then(result => {
        if (result.status === 'success') {
          this.paymentsMessaging.pushResult({
            paymentsStatus: PaymentsResultStatus.SUCCESS,
          });
        } else {
          this.paymentsMessaging.pushError({
            errorCode: result.errorCode,
            errorType: PaymentsErrorType.ERROR,
            eventId: result.requestId,
          });
        }
      });
  }
}
